import { Close } from '@mui/icons-material';
import {
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Step,
  StepLabel,
  Stepper,
} from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { BUTTON_COLOR, BUTTON_VARIANT } from 'src/components/Button/types';
import { useFacebookPosts } from 'src/hooks/useFacebookPosts';
import { CreateFacebookPostScheduleProps, GroupData, PageData } from './types';
import {
  FacebookPost,
  FacebookPostRotation,
  GroupOptions,
  PageOptions,
  PostSchedule,
} from 'src/DTO/FacebookPost.type';
import { ConfirmFacebookPostSchedule } from './ConfirmFacebookPostSchedule';
import { ScheduleFacebookPostStep1 } from './ScheduleFacebookPostStep1';
import { ScheduleFacebookPostStep2 } from './ScheduleFacebookPostStep2';
import { ScheduleFacebookPostStep3 } from './ScheduleFacebookPostStep3';
import { ThankYouPage } from './ThankYou';
import { DEFAULTS, STEPS } from './constants';
import { useStoreActions } from 'src/stores';
import { PopUpEnum } from 'src/stores/NotificationStore';
import { FacebookLoginClient, LoginResponse } from '@greatsumini/react-facebook-login';
import {
  useAuthFacebookPages,
  useGetFacebookPosts,
  useGetConnectedFacebookPages,
  useGetConnectedFacebookGroups,
  useAddFacebookGroups,
} from 'src/api/hooks/useFacebookPost';
import { useRedirect } from 'src/hooks/useRedirect';
import { ROUTES } from 'src/utils/constants.util';
import { useHistory, useLocation } from 'react-router-dom';

export const CreateFacebookPostSchedule: React.FC<CreateFacebookPostScheduleProps> = ({
  open,
  setOpen,
  selectedJob,
  setSchedulePosts,
  scheduleFacebookPost,
  getScheduledPosts,
  getJobs,
  redirectedJobId,
}) => {
  const { t } = useTranslation();

  const location = useLocation();
  const history = useHistory();

  const { handleRedirect } = useRedirect();

  const { PAGE, GROUP, PAGE_OPTIONS, GROUP_OPTIONS, POST_SCHEDULE } = DEFAULTS;

  const setPopUpData = useStoreActions(action => action.popUp.setPopUp);

  const populatedPageData = useRef<PageData[]>([]);
  const populatedGroupData = useRef<GroupData[]>([]);

  const [activeStep, setActiveStep] = useState<number>(0);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState<boolean>(false);
  const [postSchedule, setPostSchedule] = useState<PostSchedule>(POST_SCHEDULE);
  const [fbPosts, setFbPosts] = useState<FacebookPost[]>([]);

  const [pageOptions, setPageOptions] = useState<string[]>(Object.values(PageOptions));
  const [groupOptions, setGroupOptions] = useState<string[]>(Object.values(GroupOptions));

  const [pageData, setPageData] = useState<PageData[]>(PAGE);
  const [groupData, setGroupData] = useState<GroupData[]>(GROUP);

  const [isLogged, setIsLogged] = useState<boolean>(false);

  const facebookPosts = useMemo(() => postSchedule?.facebookPosts || [], [postSchedule]);

  const [getFacebookPosts, { loading: getFbPostLoading }] = useGetFacebookPosts({
    onComplete: res => {
      const posts = res?.posts ?? [];

      if (!posts.length) {
        setFbPosts([]);
        return;
      }

      setFbPosts(posts);

      const facebookPosts: FacebookPostRotation[] = posts.map((post, index) => ({
        id: post.id || '',
        postOrder: index + 1,
        postText: post.postText || '',
        postPictureUrl: post.postPictureUrl || '',
      }));

      setPostSchedule(prevState => ({
        ...prevState,
        jobId: posts[0]?.job?.id || '',
        facebookPosts,
      }));
    },
  });

  const [authFacebookPages, { loading: authFacebookPagesLoading }] = useAuthFacebookPages({
    onComplete: () => {
      getConnectedFacebookPages();
      setPopUpData({
        isOpen: true,
        type: PopUpEnum.success,
        message: t('facebookPost.scheduling.alerts.facebookAuth.success'),
      });
    },
    onError: () => {
      setPopUpData({
        isOpen: true,
        type: PopUpEnum.error,
        message: t('facebookPost.scheduling.alerts.facebookAuth.error'),
      });
    },
  });

  const [getConnectedFacebookPages, { loading: getConnectedFacebookPagesLoading }] =
    useGetConnectedFacebookPages({
      onComplete: res => {
        if (!res?.pageData?.length) return;

        const updatedData = res.pageData.filter(item => !pageOptions.includes(item.name));

        if (!updatedData.length) return;

        setPageOptions(prev => [...prev, ...updatedData.map(item => item.name)]);
        setPageData(prev => [...prev, ...updatedData]);

        setIsLogged(Boolean(res.hasConnectedPages));

        if (!populatedPageData.current.length) {
          populatedPageData.current = updatedData;
        }
      },
      onError: () => {
        setPopUpData({
          isOpen: true,
          type: PopUpEnum.error,
          message: t('facebookPost.scheduling.alerts.checkConnectedPages.error'),
        });
      },
    });

  const [getConnectedFacebookGroups] = useGetConnectedFacebookGroups({
    onComplete: res => {
      if (!res?.groupData?.length) return;

      const connectedGroups = res.groupData.filter(item => !groupOptions.includes(item.name));

      if (!connectedGroups.length) return;

      setGroupOptions(prev => [...prev, ...connectedGroups.map(item => item.name)]);

      populatedGroupData.current = [...populatedGroupData.current, ...connectedGroups];
    },
    onError: () => {
      setPopUpData({
        isOpen: true,
        type: PopUpEnum.error,
        message: t('facebookPost.scheduling.alerts.getFacebookGroups.error'),
      });
    },
  });

  const [addFacebookGroups] = useAddFacebookGroups({
    onComplete: () => {
      getConnectedFacebookGroups();
      setPopUpData({
        isOpen: true,
        type: PopUpEnum.success,
        message: t('facebookPost.scheduling.alerts.addFacebookGroup.success'),
      });
    },
    onError: () => {
      setPopUpData({
        isOpen: true,
        type: PopUpEnum.error,
        message: t('facebookPost.scheduling.alerts.addFacebookGroup.error'),
      });
    },
  });

  const {
    generateFbPost,
    setTextLoading,
    setImageLoading,
    textGenerationLoading,
    imageGenerationLoading,
    generatedText,
    generatedImage,
    generateFbPostLoading,
    setGeneratedText,
    setGeneratedImage,
  } = useFacebookPosts(getFacebookPosts, selectedJob.id);

  const handleAuthSuccess = (accessToken: string) =>
    authFacebookPages({ variables: { accessToken, jobId: selectedJob.id } });

  const reset = () => {
    logoutIfLoggedIn();

    setPostSchedule(POST_SCHEDULE);
    setGeneratedText('');
    setGeneratedImage('');
    setPageData(PAGE);
    setGroupData(GROUP);
    setPageOptions(PAGE_OPTIONS);
    setGroupOptions(GROUP_OPTIONS);
    setActiveStep(0);
  };

  const handleLogout = () => {
    FacebookLoginClient.logout(() => {
      setIsLogged(false);
      FacebookLoginClient.clear();
    });
  };

  const logoutIfLoggedIn = (): void => {
    FacebookLoginClient.getLoginStatus((res: LoginResponse) => {
      if (res.status === 'connected') {
        handleLogout();
      }
    });
  };

  const handleShowCloseConfirmDialog = () => {
    setIsConfirmDialogOpen(true);
  };

  const getJobsAndPosts = () => {
    getJobs();
    getScheduledPosts();
  };

  const clearRedirectState = () => {
    history.replace({ pathname: location.pathname, state: {} });
  };

  const handleRedirectFlow = () => {
    if (activeStep < 4) {
      handleRedirect({ route: ROUTES.JOB, id: selectedJob.id });
    } else {
      clearRedirectState();
    }
  };

  const handleNormalFlow = () => {
    if (location.pathname === ROUTES.FACEBOOK_AI) {
      getJobsAndPosts();
    }
  };

  const handleClose = () => {
    setIsConfirmDialogOpen(false);
    setOpen(false);
    reset();

    if (redirectedJobId) {
      handleRedirectFlow();
    } else {
      handleNormalFlow();
    }
  };

  const handleMoveToNextStep = () => {
    setActiveStep(prevStep => prevStep + 1);
  };

  const handleMoveToPreviousStep = () => {
    setActiveStep(prevStep => prevStep - 1);
  };

  const saveChanges = (updatedScheduleData: PostSchedule) => {
    setPostSchedule(prevScheduleData => ({
      ...prevScheduleData,
      ...updatedScheduleData,
    }));
  };

  useEffect(() => {
    if (!open || !selectedJob?.id) return;

    getFacebookPosts({ variables: { jobId: selectedJob.id } });
  }, [open, selectedJob, generatedText, generatedImage]);

  useEffect(() => {
    if (!open) return;

    getConnectedFacebookPages();
    getConnectedFacebookGroups();
  }, [open]);

  const getStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <ScheduleFacebookPostStep1
            fbPosts={fbPosts}
            generatedText={generatedText}
            imageLoading={imageGenerationLoading}
            textLoading={textGenerationLoading}
            setTextLoading={setTextLoading}
            setImageLoading={setImageLoading}
            generateFbPost={generateFbPost}
            selectedJob={selectedJob}
            getFbPostLoading={getFbPostLoading}
            onCancel={handleShowCloseConfirmDialog}
            setFbPosts={setFbPosts}
            handleMoveToNextStep={handleMoveToNextStep}
            generateFbPostLoading={generateFbPostLoading}
            facebookPosts={facebookPosts}
            redirectedJobId={redirectedJobId}
            setPostSchedule={setPostSchedule}
          />
        );
      case 1:
        return (
          <ScheduleFacebookPostStep2
            fbPosts={fbPosts}
            postSchedule={postSchedule}
            onSubmit={saveChanges}
            selectedJob={selectedJob}
            handleMoveToNextStep={handleMoveToNextStep}
            handlePreviousButtonClick={handleMoveToPreviousStep}
          />
        );
      case 2:
        return (
          <ScheduleFacebookPostStep3
            postSchedule={postSchedule}
            onSubmit={saveChanges}
            handleMoveToNextStep={handleMoveToNextStep}
            handlePreviousButtonClick={handleMoveToPreviousStep}
            pageOptions={pageOptions}
            groupOptions={groupOptions}
            pageData={pageData}
            setPageData={setPageData}
            groupData={groupData}
            setGroupData={setGroupData}
            handleAuthSuccess={handleAuthSuccess}
            authFacebookPagesLoading={authFacebookPagesLoading}
            getConnectedFacebookPagesLoading={getConnectedFacebookPagesLoading}
            setIsLogged={setIsLogged}
            isLogged={isLogged}
            populatedPageData={populatedPageData.current}
            populatedGroupData={populatedGroupData.current}
            jobId={selectedJob.id}
            addFacebookGroups={addFacebookGroups}
          />
        );
      case 3:
        return (
          <ConfirmFacebookPostSchedule
            fbPosts={fbPosts}
            postSchedule={postSchedule}
            handleClose={handleClose}
            handleMoveToNextStep={handleMoveToNextStep}
            handlePreviousButtonClick={handleMoveToPreviousStep}
            setSchedulePosts={setSchedulePosts}
            scheduleFacebookPost={scheduleFacebookPost}
          />
        );
      case 4:
        return <ThankYouPage handleClose={handleClose} />;
      default:
        return null;
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleShowCloseConfirmDialog}
        aria-labelledby="form-dialog-title"
        fullScreen>
        <DialogContent>
          <Box display="flex" justifyContent="flex-end" onClick={handleShowCloseConfirmDialog}>
            <Close style={{ cursor: 'pointer' }} />
          </Box>
          <Box pt={3} pb={8}>
            <Grid container justifyContent="center">
              <Grid item xs={6} lg={6}>
                <Stepper activeStep={activeStep} alternativeLabel>
                  {STEPS.map(label => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
                <Card sx={{ maxWidth: activeStep !== 1 ? 'xl' : 'md', margin: '0 auto' }}>
                  <Box p={4} display="flex" justifyContent="center" flexDirection="column">
                    <Box>{getStepContent()}</Box>
                  </Box>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
      </Dialog>
      <Dialog open={isConfirmDialogOpen} onClose={() => setIsConfirmDialogOpen(false)}>
        <DialogTitle>
          <Trans>jobPage.createJobForm.confirmCloseTitle</Trans>
        </DialogTitle>
        <DialogContent>
          <DialogContentText color="textPrimary">
            <Trans>jobPage.createJobForm.confirmCLoseText</Trans>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsConfirmDialogOpen(false)}>
            <Trans>generic.no</Trans>
          </Button>
          <Button onClick={handleClose} variant={BUTTON_VARIANT.GRADIENT} color={BUTTON_COLOR.DARK}>
            <Trans>generic.yes</Trans>
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
